// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React, { useState } from 'react';

import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';

import { KafkaState, ToolsContent, WizardState } from '../interfaces';
import { TOOLS_CONTENT } from '../steps-config';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Input from '@cloudscape-design/components/input';

import { InfoLink } from '../../commons/common-components';
import Slider from '@cloudscape-design/components/slider';
import RadioGroup from '@cloudscape-design/components/radio-group';
import { OptionDefinition } from '@cloudscape-design/components/internal/components/option/interfaces';
import Select from '@cloudscape-design/components/select';

const { kafka: kafkaToolsContent } = TOOLS_CONTENT;

interface InstanceOptionsProps extends KafkaState {
  setHelpPanelContent: (tools: ToolsContent) => void;
  onChange: (state: Partial<KafkaState>) => void;
}

interface KafkaDetails extends KafkaState {
  setHelpPanelContent: (tools: ToolsContent) => void;
  onDetailsChange: (state: Partial<KafkaState>) => void;
}

const KafkaDetails = ({
  consumer_topic,
  consumer_spec_config_retention_ms,
  consumer_spec_partitions,
  consumer_spec_replicas,
  consumer_spec_segment_bytes,
  producer_topic,
  producer_spec_config_retention_ms,
  producer_spec_partitions,
  producer_spec_replicas,
  producer_spec_segment_bytes,
  consumer_topic_mode = 'existing',
  producer_topic_mode  = 'existing',
  onChange,
  setHelpPanelContent,
}: InstanceOptionsProps) => {
  const validateStorageValue = (value: number, range: [number, number]) => {
    if (value === undefined) {
      return '';
    }
    if (value < range[0] || value > range[1]) {
      return `Enter a valid value greater than ${range[0]}) and less than ${range[1]}.`;
    }
    return '';
  };
  const existingTopics: OptionDefinition[] = [
    { label: 'topic-in-new-app', value: 'topic-in-new-app' },
    { label: 'topic-out-new-app', value: 'topic-out-new-app' },
  ];

  const selectedOption = existingTopics.find(t => t.value === consumer_topic) ?? null;
  const selectedProducerOption = existingTopics.find(t => t.value === producer_topic) ?? null;

  return (
    <Container header={<Header variant="h2">Kafka details</Header>}>
      <SpaceBetween size="l">
        <FormField
          label="Input Topic"
          info={<InfoLink onFollow={() => setHelpPanelContent(kafkaToolsContent.default)} />}
          // description="Choose an existing Kafka topic for this consumer or create a new one."
        >
          <SpaceBetween size="m">
            <RadioGroup
              onChange={({ detail }) => {
                onChange({
                  consumer_topic_mode: detail.value as 'existing' | 'new',
                  consumer_topic: '', // clear on switch
                });
              }}
              value={consumer_topic_mode}
              items={[
                { value: 'existing', label: 'Select existing topic' },
                { value: 'new', label: 'Create new topic' },
              ]}
            />
            {consumer_topic_mode === 'existing' ? (
              <Select
                selectedOption={selectedOption}
                onChange={({ detail }) =>
                  onChange({ consumer_topic: detail.selectedOption?.value || '' })
                }
                options={existingTopics}
                placeholder="Select a topic"
              />
            ) : (
              <SpaceBetween size="l">
                <ColumnLayout columns={1}>
                  {/* <Input
                    value={consumer_topic}
                    placeholder="Enter new topic name"
                    onChange={e => onChange({ consumer_topic: e.detail.value })}
                  /> */}
                  <FormField
                    label="Name"
                    info={<InfoLink onFollow={() => setHelpPanelContent(kafkaToolsContent.default)} />}
                    // description="A string that defines the name of the input topic this job consumes from."
                    // constraintText="Must start with a letter. Must contain 1 to 64 alphanumeric characters."
                  >
                    <Input placeholder="name-of-topic-to-consume-from" value={consumer_topic} onChange={event => onChange({ consumer_topic: event.detail.value })} />
                  </FormField>
                </ColumnLayout>
                <ColumnLayout columns={2}>
                  <FormField
                    label="Number of topic replicas"
                    // description="..."
                    errorText={validateStorageValue(Number(consumer_spec_replicas), [1, 64])}
                  >
                    <div className="flex-wrapper">
                      <div className="slider-wrapper">
                        <Slider
                          ariaLabel="Replica count slider"
                          // value={Number(resources_requests_cpu)}
                          value={Number.isNaN(Number(consumer_spec_replicas)) ? 1 : Number(consumer_spec_replicas)}
                          onChange={event => onChange({ consumer_spec_replicas: String(event.detail.value) })}
                          min={1}
                          max={64}
                        />
                      </div>
                      <SpaceBetween size="m" alignItems="center" direction="horizontal">
                        <div className="input-wrapper">
                          <Input
                            type="number"
                            autoComplete={true}
                            controlId="consumer_spec_replicas"
                            value={consumer_spec_replicas}
                            onChange={event => onChange({ consumer_spec_replicas: String(event.detail.value) })}
                          />
                        </div>
                        <div>GiB</div>
                      </SpaceBetween>
                    </div>
                  </FormField>
                  <FormField
                    label="Number of partitions"
                    // description="..."
                    errorText={validateStorageValue(Number(consumer_spec_partitions), [1, 32])}
                  >
                    <div className="flex-wrapper">
                      <div className="slider-wrapper">
                        <Slider
                          ariaLabel="Allocated storage slider"
                          // value={Number(resources_limits_cpu)}
                          value={Number.isNaN(Number(consumer_spec_partitions)) ? 1 : Number(consumer_spec_partitions)}
                          onChange={event => onChange({ consumer_spec_partitions: String(event.detail.value) })}
                          min={1}
                          max={32}
                        />
                      </div>
                      <SpaceBetween size="m" alignItems="center" direction="horizontal">
                        <div className="input-wrapper">
                          <Input
                            type="number"
                            autoComplete={true}
                            controlId="resources_requests_cpu"
                            value={consumer_spec_partitions}
                            onChange={event => onChange({ consumer_spec_partitions: String(event.detail.value) })}
                          />
                        </div>
                        <div></div>
                      </SpaceBetween>
                    </div>
                  </FormField>
                </ColumnLayout>
                <ColumnLayout columns={2}>
                  <FormField
                    label="Retention in millseconds."
                    // description="..."
                    errorText={validateStorageValue(Number(consumer_spec_config_retention_ms), [1, 10000000])}
                  >
                    <div className="flex-wrapper">
                      <div className="slider-wrapper">
                        <Slider
                          ariaLabel="Slider"
                          // value={Number(resources_limits_memory)}
                          value={Number.isNaN(Number(consumer_spec_config_retention_ms)) ? 1 : Number(consumer_spec_config_retention_ms)}
                          onChange={event => onChange({ consumer_spec_config_retention_ms: String(event.detail.value) })}
                          min={60000}
                          max={10000000}
                        />
                      </div>
                      <SpaceBetween size="m" alignItems="center" direction="horizontal">
                        <div className="input-wrapper">
                          <Input
                            type="number"
                            autoComplete={true}
                            controlId="consumer_spec_config_retention_ms"
                            value={consumer_spec_config_retention_ms}
                            onChange={event => onChange({ consumer_spec_config_retention_ms: String(event.detail.value) })}
                          />
                        </div>
                        <div>ms</div>
                      </SpaceBetween>
                    </div>
                  </FormField>
                  <FormField
                    label="Max segment size (bytes)."
                    // description="..."
                    errorText={validateStorageValue(Number(consumer_spec_segment_bytes), [536870912, 4294967296])}
                  >
                    <div className="flex-wrapper">
                      <div className="slider-wrapper">
                        <Slider
                          ariaLabel="Allocated storage slider"
                          // value={Number(resources_limits_cpu)}
                          value={Number.isNaN(Number(consumer_spec_segment_bytes)) ? 1 : Number(consumer_spec_segment_bytes)}
                          onChange={event => onChange({ consumer_spec_segment_bytes: String(event.detail.value) })}
                          min={536870912}
                          max={4294967296}
                        />
                      </div>
                      <SpaceBetween size="m" alignItems="center" direction="horizontal">
                        <div className="input-wrapper">
                          <Input
                            type="number"
                            autoComplete={true}
                            controlId="consumer_spec_segment_bytes"
                            value={consumer_spec_segment_bytes}
                            onChange={event => onChange({ consumer_spec_segment_bytes: String(event.detail.value) })}
                          />
                        </div>
                        <div>Gi</div>
                      </SpaceBetween>
                    </div>
                  </FormField>
                </ColumnLayout>
              </SpaceBetween>
            )}
          </SpaceBetween>
        </FormField>
        <FormField
          label="Destination Topic"
          info={<InfoLink onFollow={() => setHelpPanelContent(kafkaToolsContent.default)} />}
          // description="Choose an existing Kafka output topic for this producer or create a new one."
        >
          <SpaceBetween size="m">
            <RadioGroup
              onChange={({ detail }) => {
                onChange({
                  producer_topic_mode: detail.value as 'existing' | 'new',
                  producer_topic: '', // clear on switch
                });
              }}
              value={producer_topic_mode}
              items={[
                { value: 'existing', label: 'Select existing topic' },
                { value: 'new', label: 'Create new topic' },
              ]}
            />
            {producer_topic_mode === 'existing' ? (
              <Select
                selectedOption={selectedProducerOption}
                onChange={({ detail }) =>
                  onChange({ producer_topic: detail.selectedOption?.value || '' })
                }
                options={existingTopics}
                placeholder="Select a topic"
              />
            ) : (
              <SpaceBetween size="l">
                <ColumnLayout columns={1}>
                  {/* <Input
                    value={producer_topic}
                    placeholder="Enter new topic name"
                    onChange={e => onChange({ producer_topic: e.detail.value })}
                  /> */}
                  <FormField
                    label="Name"
                    info={<InfoLink onFollow={() => setHelpPanelContent(kafkaToolsContent.default)} />}
                    // description="A string that defines the name of the input topic this job consumes from."
                    // constraintText="Must start with a letter. Must contain 1 to 64 alphanumeric characters."
                  >
                    <Input placeholder="name-of-topic-to-consume-from" value={producer_topic} onChange={event => onChange({ producer_topic: event.detail.value })} />
                  </FormField>
                </ColumnLayout>
                <ColumnLayout columns={2}>
                  <FormField
                    label="Number of topic replicas"
                    // description="..."
                    errorText={validateStorageValue(Number(producer_spec_replicas), [1, 64])}
                  >
                    <div className="flex-wrapper">
                      <div className="slider-wrapper">
                        <Slider
                          ariaLabel="Replica count slider"
                          // value={Number(resources_requests_cpu)}
                          value={Number.isNaN(Number(producer_spec_replicas)) ? 1 : Number(producer_spec_replicas)}
                          onChange={event => onChange({ producer_spec_replicas: String(event.detail.value) })}
                          min={1}
                          max={64}
                        />
                      </div>
                      <SpaceBetween size="m" alignItems="center" direction="horizontal">
                        <div className="input-wrapper">
                          <Input
                            type="number"
                            autoComplete={true}
                            controlId="producer_spec_replicas"
                            value={producer_spec_replicas}
                            onChange={event => onChange({ producer_spec_replicas: String(event.detail.value) })}
                          />
                        </div>
                        <div>GiB</div>
                      </SpaceBetween>
                    </div>
                  </FormField>
                  <FormField
                    label="Number of partitions"
                    // description="..."
                    errorText={validateStorageValue(Number(producer_spec_partitions), [1, 32])}
                  >
                    <div className="flex-wrapper">
                      <div className="slider-wrapper">
                        <Slider
                          ariaLabel="Allocated storage slider"
                          // value={Number(resources_limits_cpu)}
                          value={Number.isNaN(Number(producer_spec_partitions)) ? 1 : Number(producer_spec_partitions)}
                          onChange={event => onChange({ producer_spec_partitions: String(event.detail.value) })}
                          min={1}
                          max={32}
                        />
                      </div>
                      <SpaceBetween size="m" alignItems="center" direction="horizontal">
                        <div className="input-wrapper">
                          <Input
                            type="number"
                            autoComplete={true}
                            controlId="resources_requests_cpu"
                            value={producer_spec_partitions}
                            onChange={event => onChange({ producer_spec_partitions: String(event.detail.value) })}
                          />
                        </div>
                        <div></div>
                      </SpaceBetween>
                    </div>
                  </FormField>
                </ColumnLayout>
                <ColumnLayout columns={2}>
                  <FormField
                    label="Retention in millseconds."
                    // description="..."
                    errorText={validateStorageValue(Number(producer_spec_config_retention_ms), [1, 10000000])}
                  >
                    <div className="flex-wrapper">
                      <div className="slider-wrapper">
                        <Slider
                          ariaLabel="Slider"
                          // value={Number(resources_limits_memory)}
                          value={Number.isNaN(Number(producer_spec_config_retention_ms)) ? 1 : Number(producer_spec_config_retention_ms)}
                          onChange={event => onChange({ producer_spec_config_retention_ms: String(event.detail.value) })}
                          min={60000}
                          max={10000000}
                        />
                      </div>
                      <SpaceBetween size="m" alignItems="center" direction="horizontal">
                        <div className="input-wrapper">
                          <Input
                            type="number"
                            autoComplete={true}
                            controlId="producer_spec_config_retention_ms"
                            value={producer_spec_config_retention_ms}
                            onChange={event => onChange({ producer_spec_config_retention_ms: String(event.detail.value) })}
                          />
                        </div>
                        <div>ms</div>
                      </SpaceBetween>
                    </div>
                  </FormField>
                  <FormField
                    label="Max segment size (bytes)."
                    // description="..."
                    errorText={validateStorageValue(Number(producer_spec_segment_bytes), [536870912, 4294967296])}
                  >
                    <div className="flex-wrapper">
                      <div className="slider-wrapper">
                        <Slider
                          ariaLabel="Allocated storage slider"
                          // value={Number(resources_limits_cpu)}
                          value={Number.isNaN(Number(producer_spec_segment_bytes)) ? 1 : Number(producer_spec_segment_bytes)}
                          onChange={event => onChange({ producer_spec_segment_bytes: String(event.detail.value) })}
                          min={536870912}
                          max={4294967296}
                        />
                      </div>
                      <SpaceBetween size="m" alignItems="center" direction="horizontal">
                        <div className="input-wrapper">
                          <Input
                            type="number"
                            autoComplete={true}
                            controlId="producer_spec_segment_bytes"
                            value={producer_spec_segment_bytes}
                            onChange={event => onChange({ producer_spec_segment_bytes: String(event.detail.value) })}
                          />
                        </div>
                        <div>Gi</div>
                      </SpaceBetween>
                    </div>
                  </FormField>
                </ColumnLayout>
              </SpaceBetween>
            )}
          </SpaceBetween>
        </FormField>
      </SpaceBetween>
    </Container>
  );
};
interface KafkaProps {
  info: WizardState;
  setHelpPanelContent: (tools: ToolsContent) => void;
  onChange: (state: Partial<KafkaState>) => void;
}

const Kafka = ({ info: { kafka }, setHelpPanelContent, onChange }: KafkaProps) => {
  const childProps = { ...kafka, setHelpPanelContent, onChange };
  return (
    <Box margin={{ bottom: 'l' }}>
      <SpaceBetween size="l">
        <KafkaDetails {...childProps} />
      </SpaceBetween>
    </Box>
  );
};
export default Kafka;



